import React, { useState, useEffect } from 'react';
import { TextInput, Box, Container, Title } from '@mantine/core';
import { useSocket } from '../contexts/SocketContext';
import { motion, AnimatePresence } from 'framer-motion';

const triangleColors = [
  '#152F3C', '#1B3B4A', '#204758', '#265365', '#2D5F72',
  '#336B7F', '#39778B', '#408398', '#478FA4', '#4E9CB0',
  '#5DA4B5', '#6BABBA', '#79B3BF', '#87BBC4', '#95C2CA',
  '#A2CACF', '#B0D1D5', '#BDD8DB'
];

export function DataCollection({ instance }) {
  const socket = useSocket();
  const [email, setEmail] = useState('');
  const [isSpinning, setIsSpinning] = useState(false);
  const [triangles, setTriangles] = useState([]);

  useEffect(() => {
    if (socket) {
      console.log('Socket connected:', socket.connected);
      
      socket.on('connect', () => {
        console.log('Socket connected');
        // Send a test event once connected
        socket.emit('test_event', { message: 'Hello from client' });
      });

      socket.on('disconnect', () => {
        console.log('Socket disconnected');
      });

      socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });

      socket.on('test_response', (data) => {
        console.log('Received test response:', data);
      });
    } else {
      console.log('Socket not available');
    }
  }, [socket]);

  useEffect(() => {
    if (isSpinning) {
      let count = 0;
      const createTriangle = () => {
        const newTriangle = {
          id: Date.now(),
          color: triangleColors[Math.floor(Math.random() * triangleColors.length)],
          delay: count < 6 ? 0.03 * count : 0.3 + Math.random() * 0.3 // Faster delays
        };
        setTriangles(prev => [...prev, newTriangle]);
        count++;

        if (count < 6) {
          setTimeout(createTriangle, 40); // Even faster interval for first 8 triangles
        } else {
          setTimeout(createTriangle, 300 + Math.random() * 200); // Faster normal interval
        }
      };

      createTriangle();

      return () => {
        const highestId = setTimeout(() => {}, 0);
        for (let i = highestId; i >= 0; i--) {
          clearTimeout(i);
        }
      };
    } else {
      setTriangles([]);
    }
  }, [isSpinning]);

  const handleSpin = async () => {
    if (socket && email) {
      console.log('Attempting to emit spin_wheel event');
      socket.emit('spin_wheel', { email, instance });
      console.log('Emitted spin_wheel event with email:', email, 'and instance:', instance);
      
      setIsSpinning(true);
      
      // Reset after 5 seconds
      setTimeout(() => {
        setIsSpinning(false);
        setEmail('');
      }, 5000);
    } else if (!socket) {
      console.error('Socket connection not established');
    } else {
      console.error('Email is required');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSpin();
  };

  return (
    <Container 
      fluid
      p={0}
      style={{ 
        height: '100vh', 
        width: '100vw',
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <AnimatePresence>
        {isSpinning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              overflow: 'hidden',
            }}
          >
            {triangles.map((triangle) => (
              <motion.div
                key={triangle.id}
                style={{
                  width: '140vw',
                  height: '220vh',
                  position: 'absolute',
                  bottom: '-220vh',
                  left: '-20vw',
                  overflow: 'hidden',
                }}
                initial={{ y: 0, opacity: 1, scale: 1 }}
                animate={{ 
                  y: '-280vh',
                  opacity: [1, 1, 0],
                  scale: [1, 1.02, 1.05],
                }}
                transition={{ 
                  y: {
                    type: "tween",
                    ease: "easeInOut",
                    duration: 3, // Reduced from 4 to 3 seconds
                  },
                  opacity: {
                    duration: 3, // Reduced from 4 to 3 seconds
                    times: [0, 0.8, 1]
                  },
                  scale: {
                    duration: 3, // Reduced from 4 to 3 seconds
                    times: [0, 0.5, 1]
                  },
                  delay: triangle.delay
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '54.55%',
                    backgroundColor: triangle.color,
                    clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                  }}
                />
                <div
                  style={{
                    width: '100%',
                    height: '45.45%',
                    backgroundColor: triangle.color,
                  }}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {!isSpinning && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              mx="auto"
              style={{
                maxWidth: 420,
                width: '90%',
                padding: '2.5rem 2rem',
                borderRadius: '16px',
                background: 'rgba(230, 230, 230, 0.82)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.25)',
              }}
            >
              <motion.img 
                src="/ultimateguard.svg" 
                alt="Ultimate Guard Logo" 
                style={{ 
                  width: '100%', 
                  maxWidth: '180px', 
                  margin: '0 auto 2rem', 
                  display: 'block' 
                }} 
              />
              <motion.div>
                <Title 
                  order={2} 
                  align="center" 
                  mb="xl" 
                  style={{ 
                    fontFamily: 'Figtree, sans-serif',
                    fontStyle: 'italic',
                    textTransform: 'uppercase',
                    color: '#222221',
                    fontWeight: 700,
                  }}
                >
                  Enter Your Email to Play
                </Title>
              </motion.div>
              <motion.form onSubmit={handleSubmit}>
                <TextInput
                  required
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  size="xl"
                  placeholder="Your email"
                  mb="md"
                  styles={() => ({
                    input: {
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      textAlign: 'center',
                      fontFamily: 'Roboto, sans-serif',
                      border: '2px solid transparent',
                      transition: 'border-color 0.3s ease',
                      '&:focus, &:focus-visible, &:focus-within': {
                        backgroundColor: 'rgba(255,255,255,1) !important',
                        borderColor: '#CD71D5',
                        outline: 'none',
                      },
                    },
                  })}
                />
                <motion.button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    outline: 'none',
                    background: 'linear-gradient(45deg, #163456, #2B657B)',
                    transition: 'background 0.3s ease, box-shadow 0.3s ease',
                    textTransform: 'uppercase',
                    fontFamily: 'Roboto, sans-serif',
                  }}
                >
                  Roll to Win
                </motion.button>
              </motion.form>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  );
}
